import { ReactSketchCanvas } from 'react-sketch-canvas';
import { useEffect, useRef, useState } from 'react';

const colors = ['#111111', '#e11d48', '#2563eb', '#16a34a', '#f59e0b', '#7c3aed'];
const backgrounds = [
  { label: 'White', value: '#ffffff' },
  { label: 'Paper', value: '#fff4cf' },
  { label: 'Dark', value: '#111827' },
];

function App() {
  const canvas = useRef(null);
  const [color, setColor] = useState(colors[0]);
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const [canvasColor, setCanvasColor] = useState(backgrounds[0].value);
  const [showGrid, setShowGrid] = useState(false);

  useEffect(() => {
    canvas.current?.eraseMode(isEraser);
  }, [isEraser]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = event.key.toLowerCase();

      if (event.ctrlKey && key === 'z') {
        event.preventDefault();
        canvas.current?.undo();
        return;
      }

      if (event.ctrlKey && key === 'y') {
        event.preventDefault();
        canvas.current?.redo();
        return;
      }

      if (key === 'e') {
        setIsEraser(true);
        return;
      }

      if (key === 'b') {
        setIsEraser(false);
        return;
      }

      if (key === '[') {
        setStrokeWidth((width) => Math.max(1, width - 1));
        return;
      }

      if (key === ']') {
        setStrokeWidth((width) => Math.min(32, width + 1));
        return;
      }

      if (/^[1-6]$/.test(key)) {
        setColor(colors[Number(key) - 1]);
        setIsEraser(false);
        return;
      }

      if (key === 'g') {
        setShowGrid((visible) => !visible);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const selectColor = (nextColor) => {
    setColor(nextColor);
    setIsEraser(false);
  };

  const exportPng = async () => {
    const image = await canvas.current?.exportImage('png');

    if (!image) {
      return;
    }

    const link = document.createElement('a');
    link.href = image;
    link.download = 'drawing.png';
    link.click();
  };

  return (
    <main className="app">
      <header className="toolbar" aria-label="Drawing toolbar">
        <div className="toolbar-section" role="group" aria-label="Drawing mode">
          <button
            className={!isEraser ? 'active' : ''}
            onClick={() => setIsEraser(false)}
            type="button"
          >
            Brush
          </button>
          <button
            className={isEraser ? 'active' : ''}
            onClick={() => setIsEraser(true)}
            type="button"
          >
            Eraser
          </button>
        </div>

        <div className="toolbar-section colors" role="group" aria-label="Colors">
          {colors.map((swatch, index) => (
            <button
              aria-label={`Color ${index + 1}`}
              className={color === swatch && !isEraser ? 'swatch active' : 'swatch'}
              key={swatch}
              onClick={() => selectColor(swatch)}
              style={{ backgroundColor: swatch }}
              title={`Color ${index + 1}`}
              type="button"
            />
          ))}
          <input
            aria-label="Custom color"
            className="custom-color"
            onChange={(event) => selectColor(event.target.value)}
            type="color"
            value={color}
          />
        </div>

        <label className="size-control">
          <span>Size</span>
          <input
            aria-label="Brush size"
            max="32"
            min="1"
            onChange={(event) => setStrokeWidth(Number(event.target.value))}
            type="range"
            value={strokeWidth}
          />
          <output>{strokeWidth}</output>
        </label>

        <div className="toolbar-section canvas-options" role="group" aria-label="Canvas options">
          <select
            aria-label="Canvas background"
            onChange={(event) => setCanvasColor(event.target.value)}
            value={canvasColor}
          >
            {backgrounds.map((background) => (
              <option key={background.value} value={background.value}>
                {background.label}
              </option>
            ))}
          </select>
          <button
            className={showGrid ? 'active' : ''}
            onClick={() => setShowGrid((visible) => !visible)}
            title="G"
            type="button"
          >
            Grid
          </button>
        </div>

        <div className="toolbar-section actions" role="group" aria-label="Actions">
          <button onClick={() => canvas.current?.undo()} type="button">
            Undo
          </button>
          <button onClick={() => canvas.current?.redo()} type="button">
            Redo
          </button>
          <button onClick={() => canvas.current?.clearCanvas()} type="button">
            Clear
          </button>
          <span className="toolbar-divider" aria-hidden="true" />
          <button onClick={exportPng} type="button">
            PNG
          </button>
        </div>
      </header>

      <section className="canvas-area" aria-label="Drawing canvas">
        <div className={showGrid ? 'canvas-shell grid-on' : 'canvas-shell'}>
          <ReactSketchCanvas
            ref={canvas}
            strokeWidth={strokeWidth}
            strokeColor={color}
            eraserWidth={strokeWidth * 4}
            canvasColor={canvasColor}
            className="sketch-canvas"
          />
        </div>
      </section>
    </main>
  );
}

export default App;
